import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const stored = localStorage.getItem("sg_cart");
            return stored ? JSON.parse(stored) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem("sg_cart", JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1, specialPrice = null) => {
        setItems(prev => {
            const itemKey = specialPrice ? `${product.id}_bp` : product.id;
            const existing = prev.find(i => i.product_id === itemKey);
            if (existing) {
                return prev.map(i =>
                    i.product_id === itemKey
                        ? { ...i, quantity: i.quantity + quantity }
                        : i
                );
            }
            return [...prev, {
                product_id: itemKey,
                original_product_id: product.id,
                sku: product.sku || "",
                name: product.name,
                price: specialPrice || product.sale_price || product.price,
                original_price: product.original_price || product.price,
                image: product.main_image || (product.images && product.images[0]) || "",
                quantity,
                stock_status: product.stock_status,
                special_price: !!specialPrice,
                better_price_id: product.better_price_id || null,
                shipping_restrictions: product.shipping_restrictions || [],
            }];
        });
    };

    const removeItem = (productId) => {
        setItems(prev => prev.filter(i => i.product_id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeItem(productId);
            return;
        }
        setItems(prev => prev.map(i =>
            i.product_id === productId ? { ...i, quantity } : i
        ));
    };

    const clearCart = () => setItems([]);

    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

    return (
        <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
